"use client";

import { Branding, Collections } from "@/types/branding";
import { useId } from "react";
import "./collections-tags-select.scss";
import { useCollectionsTagsSelect } from "./hooks/use-collections-tags-select";
import { useLocale } from "@/context/LocaleContext";

interface CollectionsTagsSelectProps {
  branding: Branding;
  collections: Collections;
  selectedCollections: string[];
  onChange: (collections: string[]) => void;
  placeholder?: string;
  label?: string;
}

export default function CollectionsTagsSelect({
  branding,
  collections,
  selectedCollections,
  onChange,
  placeholder = "Select libraries",
  label = "Select libraries",
}: CollectionsTagsSelectProps) {
  const {
    state,
    data,
    refs,
    containerEventHandlers,
    inputEventHandlers,
    listEventHandlers,
  } = useCollectionsTagsSelect({
    collections,
    selectedCollections,
    onChange,
  });
  const { locale } = useLocale();
  const { focused, setFocused, isOpen, setIsOpen, searchTerm, setSearchTerm } =
    state;
  const { filteredEntries } = data;
  const { dropdownRef, inputRef } = refs;
  const {
    handleOnContainerBlur,
    handleOnContainerClick,
    handleOnContainerFocus,
    handleOnContainerKeyDown,
  } = containerEventHandlers;
  const { handleInputClick, handleInputFocus, handleInputKeyDown } =
    inputEventHandlers;
  const { handleAdd, handleOnListItemKeyDown, handleRemove } =
    listEventHandlers;
  const id = useId();
  const listboxId = `${id}-listbox`;
  const inputId = `${id}-input`;

  return (
    <div
      className="collections-tags-select"
      ref={dropdownRef}
      onKeyDown={handleOnContainerKeyDown}
      onFocus={handleOnContainerFocus}
      onClick={handleOnContainerClick}
      onBlur={handleOnContainerBlur}
    >
      <label htmlFor={inputId} className="visually-hidden">
        {label}
      </label>

      <div
        className="collections-tags-select__container"
        style={{
          outline: focused ? `solid 2px ${branding.main_color}` : "",
        }}
      >
        <div className="collections-tags-select__tags-area">
          {selectedCollections.map((key) => (
            <span
              key={key}
              className="collections-tags-select__tag"
              style={{
                backgroundColor: "transparent",
                borderColor: branding.main_color,
                borderWidth: "2px",
                color: branding.main_color,
              }}
            >
              <span className={`collections-tags-select__tag-badge`}>
                {key.toUpperCase()}
              </span>
              <button
                type="button"
                className="collections-tags-select__tag-remove"
                onClick={(event) => handleRemove(key, event)}
                aria-label={`Remove ${collections[key]}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 4L4 10M4 4L10 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            className={`collections-tags-select__input ${
              !focused && selectedCollections.length > 0 && "hidden"
            }`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            onClick={handleInputClick}
            onKeyDown={handleInputKeyDown}
            placeholder={
              selectedCollections.length === 0 ? placeholder : undefined
            }
            aria-label={label}
            aria-controls={isOpen ? listboxId : undefined}
            aria-describedby={`${id}-description`}
            autoComplete="off"
          />
        </div>

        <button
          type="button"
          className="collections-tags-select__toggle"
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen((prev) => {
              const next = !prev;
              if (next) setFocused(true);
              return next;
            });
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              setIsOpen(false);
              inputRef.current?.focus();
            }
          }}
          aria-label="Toggle dropdown"
          aria-expanded={isOpen}
          aria-controls={listboxId}
        >
          <svg
            className={`collections-tags-select__arrow ${
              isOpen ? "collections-tags-select__arrow--open" : ""
            }`}
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L6 6L11 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <span id={`${id}-description`} className="visually-hidden">
        Type to search libraries. Press backspace to remove selected items.
        Press escape to close.
      </span>

      {isOpen && (
        <div
          id={listboxId}
          className="collections-tags-select__dropdown"
          role="listbox"
          aria-label="Available libraries"
        >
          {filteredEntries.length === 0 ? (
            <div className="collections-tags-select__no-results">
              No libraries found
            </div>
          ) : (
            filteredEntries.map(([key, value]) => {
              const isSelected = selectedCollections.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  tabIndex={0}
                  className={`collections-tags-select__option ${
                    isSelected
                      ? "collections-tags-select__option--selected"
                      : ""
                  }`}
                  style={{
                    backgroundColor: isSelected
                      ? branding.table_header_color
                      : "",
                    color: branding.main_color,
                  }}
                  onClick={() =>
                    isSelected ? handleRemove(key) : handleAdd(key)
                  }
                  onKeyDown={handleOnListItemKeyDown}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="collections-tags-select__option-content">
                    <span className={`collections-tags-select__option-badge`}>
                      {key.toUpperCase()}
                    </span>
                    <span className="collections-tags-select__option-text">
                      {value.localized_names[locale] || value.name}
                    </span>
                  </div>
                  {isSelected && (
                    <svg
                      className="collections-tags-select__check"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      style={{ color: branding.main_color }}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 8L6 11L13 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
